/**
 * Avatar composition: which layers an avatar has, in what order, and how
 * each is drawn. Shared by the LitSquad editor's live preview (browser
 * canvas) and the server renderer (`@napi-rs/canvas`), so a saved avatar
 * is drawn by exactly the rules the player previewed.
 */

import type { AccessoryTransform } from './accessory-transforms.generated';
import { type AvatarDrawContext, drawAccessoryWithTransform } from './coordinates';
import { lookupAccessoryTransform } from './transforms';

/** Edge length of the full-size avatar PNG. */
export const AVATAR_FULL_SIZE_PX = 512;

/** Edge length of the chat-thumbnail PNG. */
export const AVATAR_THUMBNAIL_SIZE_PX = 128;

/** One layer to draw, before its art is loaded. */
export interface PlannedAvatarLayer {
  /** Where the layer's art lives (a URL or storage key — the caller's choice). */
  readonly artUri: string;
  /** Per-character Unity transform; `null` draws the art stretched to fill. */
  readonly transform: AccessoryTransform | null;
}

export interface AvatarLayerPlanInput {
  /** The base character's species (any case). */
  readonly species: string | null;
  readonly base: { readonly artUri: string };
  /** In the order the player layered them. */
  readonly accessories: readonly { readonly artUri: string; readonly identifier: string | null }[];
}

/**
 * The avatar's layers in DRAW order. The base character is always first
 * (beneath everything). Accessories follow by Unity `spriteLayerNumber`, so
 * wings (sortingOrder 1) sit behind the body's accessories and held items
 * (400+) on top; an accessory with no transform keeps its layering position.
 * The sort is stable, so ties keep the player's order.
 */
export function planAvatarLayers(input: AvatarLayerPlanInput): PlannedAvatarLayer[] {
  const accessories = input.accessories.map((accessory, index) => {
    const transform = lookupAccessoryTransform(accessory.identifier, input.species);
    return {
      artUri: accessory.artUri,
      transform,
      order: transform?.spriteLayerNumber ?? index,
    };
  });
  accessories.sort((a, b) => a.order - b.order);
  return [
    { artUri: input.base.artUri, transform: null },
    ...accessories.map(({ artUri, transform }) => ({ artUri, transform })),
  ];
}

/** A planned layer with its art loaded. */
export interface LoadedAvatarLayer<TImage> {
  readonly image: TImage;
  /** Natural size of `image`; 0 when unknown. */
  readonly width: number;
  readonly height: number;
  readonly transform: AccessoryTransform | null;
}

/**
 * Draws the layers (already in draw order, as {@link planAvatarLayers}
 * returns them) onto a `size × size` canvas. A layer with no transform — the
 * base character, or an accessory with no placement data — is stretched to
 * fill, since that art is authored to the square frame. So is a layer whose
 * natural size is unknown, rather than drawing nothing.
 */
export function drawAvatarLayers<TImage>(
  ctx: AvatarDrawContext<TImage>,
  layers: readonly LoadedAvatarLayer<TImage>[],
  size: number,
): void {
  for (const layer of layers) {
    if (layer.transform === null || !layer.width || !layer.height) {
      ctx.drawImage(layer.image, 0, 0, size, size);
      continue;
    }
    drawAccessoryWithTransform(ctx, layer.image, layer.width, layer.height, layer.transform, size);
  }
}
